import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { analytics } from "../../helpers/analytics";
import { trackFunnelLead, trackStartFunnelCustomEvent } from "../../helpers/metaPixel";

import animeImg from "../../assets/images/funnel/anime_preference.webp";
import portraitImg from "../../assets/images/funnel/portrait_preference.webp";
import tattooImg from "../../assets/images/funnel/tattoo_preference.webp";
import letteringImg from "../../assets/images/funnel/lettering_preference.webp";
import textureImg from "../../assets/images/funnel/texture_preference.webp";
import traditionalImg from "../../assets/images/funnel/traditional_preference.webp";
import animalsImg from "../../assets/images/funnel/animals_preference.webp";
import comicsImg from "../../assets/images/funnel/comics_preference.webp";
import mayaAvatar from "../../assets/images/funnel/review-maya.webp";
import kenjiAvatar from "../../assets/images/funnel/review-kenji.webp";
import sofiaAvatar from "../../assets/images/funnel/review-sofia.webp";
import emmaAvatar from "../../assets/images/funnel/review-emma.webp";

/**
 * The warm-up stages that sit between the showcase reel and the payoff:
 * questions → "building your pack" → email.
 *
 * The montage funnel dropped these when it replaced the quiz funnel, and took
 * the qualification with them: it now sends three times as many people to the
 * checkout button without moving revenue. These stages exist to make a visitor
 * invest something before being asked to pay, and to leave us an email for the
 * ones who don't.
 */

export type QuizAnswers = Record<string, string>;

export const EMAIL_KEY = "funnel_email";
export const STYLE_KEY = "funnel_style";

type StyleOption = { image: string; emoji: string; label: string; value: string };

// Mirrors the direction step of the iOS app's artist journey, including its
// most-chosen answer by a wide margin: "still exploring". The web quiz used to
// force a style, which dead-ends every visitor who hasn't settled on one yet.
const STYLE_OPTIONS: StyleOption[] = [
  { image: animeImg, emoji: "🧝", label: "Characters & anime", value: "anime" },
  { image: portraitImg, emoji: "🙂", label: "Portraits & people", value: "portraits" },
  { image: traditionalImg, emoji: "🏞", label: "Nature & landscapes", value: "landscapes" },
  { image: letteringImg, emoji: "✍️", label: "Lettering", value: "lettering" },
  { image: tattooImg, emoji: "💀", label: "Tattoo & ink", value: "tattoo" },
  { image: textureImg, emoji: "🌀", label: "Textures & abstract", value: "everything" },
  // Real work from our own library — the Wild Animals and Cartoon Templates
  // covers, cropped past their title text. Generated art would have been faster
  // and wrong: these cards sit where the visitor reads them as what our brushes
  // produce.
  { image: animalsImg, emoji: "🐾", label: "Animals", value: "animals" },
  { image: comicsImg, emoji: "💥", label: "Comics & cartoon", value: "comics" },
];

/** The one answer with nothing to depict — and the app's most chosen. */
const STYLE_EXTRAS: QuizOption[] = [
  { emoji: "🔍", label: "Still exploring — surprise me", value: "exploring" },
];

type QuizOption = { emoji: string; label: string; value: string; image?: string };
type QuizQuestion = {
  id: string;
  title: string;
  subtitle?: string;
  options: QuizOption[];
  /** Only asked when this returns true — lets a branch skip itself. */
  showIf?: (answers: QuizAnswers) => boolean;
};

const QUESTIONS: QuizQuestion[] = [
  // Intent before identity. Everything below this asks who the visitor is —
  // level, frequency, motivation — and none of it asks what they came for.
  // People arrive from a reel wanting one specific brush, occasionally a pack,
  // and we were opening with their drawing style and closing with their star
  // sign. It shows in the answers: only 12% name "no right brushes" as their
  // blocker, because we never asked what they were looking for.
  //
  // This question also splits three intents we have been averaging together —
  // someone hunting one brush, someone shopping packs, and someone browsing —
  // which are worth very different things and behave nothing alike.
  {
    id: "intent",
    title: "What brought you here?",
    subtitle: "So we start with the right thing",
    options: [
      { emoji: "🖌", label: "A specific brush", value: "brush" },
      { emoji: "📦", label: "A brush pack or two", value: "pack" },
      { emoji: "🔤", label: "Fonts or lettering", value: "font" },
      { emoji: "👀", label: "Just having a look", value: "browsing" },
    ],
  },
  // Only for the two answers it makes sense for. A browser has nothing
  // specific in mind by definition, and someone after fonts is not choosing a
  // brush type — asking either would be the same mistake one question later.
  // The values match the categories on the first screen, so the answer maps
  // onto something we can actually hand back.
  {
    id: "brush_kind",
    title: "Which kind, roughly?",
    subtitle: "We'll put it at the top of your pack",
    showIf: (answers) => answers.intent === "brush" || answers.intent === "pack",
    options: [
      { emoji: "✏️", label: "Lineart & inking", value: "lineart" },
      { emoji: "💇", label: "Hair & fur", value: "hair" },
      { emoji: "🌫", label: "Texture & grain", value: "texture" },
      { emoji: "💧", label: "Watercolor & gouache", value: "watercolor" },
      { emoji: "🖐", label: "Painting & blending", value: "painting" },
      { emoji: "✨", label: "Glow & effects", value: "effects" },
      { emoji: "🤷", label: "Show me what's popular", value: "popular" },
    ],
  },
  {
    id: "style",
    title: "What do you love to draw?",
    subtitle: "We'll match brushes to your style",
    options: [], // rendered from STYLE_OPTIONS as image cards
  },
  {
    id: "level",
    title: "Where are you right now?",
    subtitle: "No wrong answer — it sets where we start you",
    // Plain tiles, like every other question. Illustrating the ladder meant
    // generating art, and generated art is the one thing this audience will not
    // forgive on a page selling them brushes — whatever it depicts, what it says
    // about us outweighs it. The style question keeps its images because those
    // are real covers from our own library.
    options: [
      { emoji: "🌱", label: "Never really drawn", value: "never_drawn" },
      { emoji: "🖊", label: "Beginner", value: "beginner" },
      { emoji: "✏️", label: "I sketch sometimes", value: "sketch" },
      { emoji: "🎨", label: "Confident hobbyist", value: "hobbyist" },
      { emoji: "⭐️", label: "Advanced", value: "advanced" },
      { emoji: "👑", label: "Full-time pro", value: "full_time_pro" },
    ],
  },
  {
    id: "frequency",
    title: "How often do you draw?",
    options: [
      { emoji: "🔥", label: "Every day", value: "every_day" },
      { emoji: "📅", label: "A few times a week", value: "few_week" },
      { emoji: "🗓", label: "Once a week", value: "once_week" },
      { emoji: "🌙", label: "Once a month", value: "once_month" },
      { emoji: "⏳", label: "Rarely", value: "rarely" },
    ],
  },
  {
    id: "blocker",
    title: "What gets in the way?",
    subtitle: "Be honest — this is what we'll fix first",
    options: [
      { emoji: "💡", label: "Running out of ideas", value: "ideas" },
      { emoji: "🎯", label: "Getting better", value: "getting_better" },
      { emoji: "🚀", label: "Actually starting", value: "starting" },
      { emoji: "🧰", label: "Not having the right tools", value: "tools" },
      { emoji: "✅", label: "Finishing what I start", value: "finishing" },
      { emoji: "📆", label: "Drawing often enough", value: "drawing_often" },
    ],
  },
  {
    id: "motivation",
    title: "What are you drawing for?",
    options: [
      { emoji: "😄", label: "Fun", value: "fun" },
      { emoji: "🎨", label: "Express myself", value: "express" },
      { emoji: "💡", label: "Bring ideas to life", value: "ideas" },
      { emoji: "💼", label: "Grow my career", value: "career" },
      { emoji: "🧘", label: "Relax", value: "relax" },
      { emoji: "📈", label: "Get better", value: "improve" },
    ],
  },
  // Asked after the visitor has already invested six answers, and before the
  // price. Someone who has bought brushes before has crossed the paying line
  // once and tells us what a normal amount looks like to them — the one thing
  // we've never known about this audience while guessing at $29.99.
  {
    id: "paid_before",
    title: "Have you ever paid for brushes?",
    subtitle: "No judgement — most people haven't",
    options: [
      { emoji: "💳", label: "Yes, I've bought some", value: "yes" },
      { emoji: "🆓", label: "Only free ones so far", value: "no" },
    ],
  },
  {
    id: "paid_amount",
    title: "Roughly how much, in total?",
    subtitle: "Ballpark is fine",
    showIf: (answers) => answers.paid_before === "yes",
    options: [
      { emoji: "🪙", label: "Under $10", value: "under_10" },
      { emoji: "💵", label: "$10–30", value: "10_30" },
      { emoji: "💰", label: "$30–100", value: "30_100" },
      { emoji: "🏆", label: "Over $100", value: "over_100" },
      { emoji: "🤔", label: "Don't remember", value: "unsure" },
    ],
  },
  {
    id: "age",
    title: "How old are you?",
    subtitle: "Artists at different stages reach for different tools",
    options: [
      // One bucket rather than splitting out under-13s. Knowing a visitor is a
      // minor is all the answer is for, and asking a child to tick a box that
      // singles them out invites a different set of obligations for a
      // distinction we never act on.
      { emoji: "🎒", label: "Under 18", value: "under_18" },
      { emoji: "✨", label: "18–24", value: "18_24" },
      { emoji: "🎨", label: "25–34", value: "25_34" },
      { emoji: "💼", label: "35–44", value: "35_44" },
      { emoji: "⭐️", label: "45+", value: "45_plus" },
      { emoji: "🤐", label: "Rather not say", value: "prefer_not_to_say" },
    ],
  },
  {
    id: "gender",
    title: "How do you identify?",
    options: [
      { emoji: "👩", label: "Female", value: "female" },
      { emoji: "👨", label: "Male", value: "male" },
      { emoji: "🌈", label: "Non-binary", value: "non_binary" },
      { emoji: "🤐", label: "Rather not say", value: "prefer_not_to_say" },
    ],
  },
  {
    id: "attribution",
    title: "Where did you hear about us?",
    subtitle: "Last one — then we build your pack",
    // This question is the only sight we have of TikTok: its in-app browser
    // strips the referrer, so 80% of the people who name it arrive as "direct".
    // Facebook was missing while sending real traffic — a tenth of the visitors
    // arrive with a facebook referrer, and they had nowhere to say so.
    options: [
      { emoji: "🎵", label: "TikTok", value: "tiktok" },
      { emoji: "📸", label: "Instagram", value: "instagram" },
      { emoji: "👍", label: "Facebook", value: "facebook" },
      { emoji: "▶️", label: "YouTube", value: "youtube" },
      { emoji: "📌", label: "Pinterest", value: "pinterest" },
      { emoji: "🛒", label: "App Store", value: "app_store" },
      { emoji: "🔎", label: "Search", value: "search" },
      { emoji: "👥", label: "A friend", value: "friend" },
      { emoji: "🔗", label: "Somewhere else", value: "other" },
    ],
  },
];

/** Human-readable label for a stored answer, for the profile card. */
const labelFor = (questionId: string, value?: string): string => {
  if (!value) return "";
  if (questionId === "style") {
    return (
      STYLE_OPTIONS.find((o) => o.value === value)?.label ||
      STYLE_EXTRAS.find((o) => o.value === value)?.label ||
      ""
    );
  }
  const q = QUESTIONS.find((x) => x.id === questionId);
  return q?.options.find((o) => o.value === value)?.label || "";
};

/**
 * The diagnosis handed back in exchange for answering. Reflecting the visitor's
 * own answers as a profile is what makes the questions feel like a reading
 * rather than a form — the same trade Glam makes with its skin score.
 */
export const profileFor = (answers: QuizAnswers) => ({
  style: labelFor("style", answers.style),
  level: labelFor("level", answers.level),
  frequency: labelFor("frequency", answers.frequency),
  gap: labelFor("blocker", answers.blocker),
  motivation: labelFor("motivation", answers.motivation),
  // What they said they came for, in their own words, read back first. The
  // star sign that used to sit here was the least useful line in the profile:
  // it proved we had asked a question, not that we had understood anything.
  lookingFor: labelFor("brush_kind", answers.brush_kind) || labelFor("intent", answers.intent),
  pack: packFor(answers.style),
});

export const QUESTION_COUNT = QUESTIONS.length;

export const STYLE_IMAGES: Record<string, string> = {
  portraits: portraitImg,
  anime: animeImg,
  lettering: letteringImg,
  landscapes: traditionalImg,
  tattoo: tattooImg,
  everything: textureImg,
};

const PACKS: Record<string, { name: string; count: number }> = {
  portraits: { name: "Portrait Pro", count: 84 },
  anime: { name: "Anime & Manga", count: 92 },
  lettering: { name: "Lettering Master", count: 76 },
  landscapes: { name: "Illustration", count: 81 },
  tattoo: { name: "Tattoo Artist", count: 68 },
  animals: { name: "Animals & Creatures", count: 74 },
  comics: { name: "Comics & Cartoon", count: 88 },
  everything: { name: "All-Style Starter", count: 120 },
};

export const packFor = (style?: string) => PACKS[style || ""] || PACKS.everything;

export const styleImageFor = (style?: string) =>
  STYLE_IMAGES[style || ""] || textureImg;

// A real stroke being laid down on a real iPad, in place of the shape we used
// to draw synthetically. The screen's whole job is to make the pack feel made
// rather than handed out, and a hand with a pencil does that in a way no
// animation of ours will. It also puts an iPad in frame, which quietly says who
// this product is for — a third of our traffic arrives on Android and cannot
// run Procreate at all.
const BUILDING_VIDEO_SRC = "/funnel/building.mp4";
const BUILDING_POSTER_SRC = "/funnel/building-poster.jpg";

const BuildingClip = () => (
  <div className="rounded-2xl overflow-hidden border border-main-grey-300 bg-main-grey-200 mb-6 aspect-[4/5] max-h-56 mx-auto">
    <video
      className="w-full h-full object-cover"
      autoPlay
      muted
      loop
      playsInline
      preload="auto"
      poster={BUILDING_POSTER_SRC}
      aria-hidden="true"
    >
      <source src={BUILDING_VIDEO_SRC} type="video/mp4" />
    </video>
  </div>
);

/**
 * Fetched while the visitor is still answering questions, so it is already in
 * cache by the time the wait begins. Six seconds of blank rectangle would be
 * the exact opposite of what that screen is for, and a minute of quiz is more
 * than enough time to have the file ready.
 */
const BuildingClipPreload = () => (
  <video
    src={BUILDING_VIDEO_SRC}
    preload="auto"
    muted
    playsInline
    aria-hidden="true"
    tabIndex={-1}
    style={{ position: "absolute", width: 0, height: 0, opacity: 0, pointerEvents: "none" }}
  />
);

const LOADING_STEPS = [
  "Analyzing your art style…",
  "Matching brushes from 10,000+ library…",
  "Adding courses for your goal…",
  "Curating your personal pack…",
];

// 202 frames at 30fps. Re-measure with ffprobe if the clip is ever re-cut.
const CLIP_MS = 6733;

// Four steps plus a closing beat, stretched to fill the clip exactly, so the
// wait ends on the last frame instead of part-way through a second play. A
// visible restart announces "this is a short loop", which is the opposite of
// what putting real footage here is meant to say. Derived rather than hardcoded
// so re-cutting the clip only means changing the number above.
const STEP_MS = Math.round(CLIP_MS / (LOADING_STEPS.length + 1));

const TESTIMONIALS = [
  {
    name: "Maya R.",
    text: "The brushes completely changed my portraits. I finally stopped fighting with the defaults.",
    style: "portraits",
    image: mayaAvatar,
  },
  {
    name: "Kenji T.",
    text: "Best anime brush set I've found — my linework looks clean straight away.",
    style: "anime",
    image: kenjiAvatar,
  },
  {
    name: "Sofia L.",
    text: "I sold my first commission two weeks after getting the lettering pack.",
    style: "lettering",
    image: sofiaAvatar,
  },
  {
    name: "Emma W.",
    text: "New brushes every week keeps me actually drawing every day.",
    style: "everything",
    image: emmaAvatar,
  },
];

/** Social proof lands harder when it matches the style the visitor just picked. */
export const testimonialFor = (style?: string) =>
  TESTIMONIALS.find((t) => t.style === style) || TESTIMONIALS[3];

const Stars = ({ size = 14 }: { size?: number }) => (
  <span
    className="text-[#FFB800] tracking-tight"
    style={{ fontSize: size }}
    aria-label="5 stars"
  >
    ★★★★★
  </span>
);

type Props = {
  profileId: string;
  /** Fires when the visitor has answered everything and left an email. */
  onComplete: (answers: QuizAnswers, email: string) => void;
  /** Lets the host draw its own progress bar. */
  onProgress?: (percent: number) => void;
  /** Back out of the first question — returns to whatever came before. */
  onExit?: () => void;
  /**
   * Fires on every stage change so the host can time things against the
   * warm-up — the countdown announcement in particular, which lands very
   * differently before the email form than it does over question one.
   */
  onStage?: (stage: "quiz" | "loading" | "email") => void;
  /**
   * Draw the first question as emoji tiles instead of eight photographs.
   * A third of everyone who starts the quiz leaves on that one screen, and it
   * is the only screen that waits on eight images before it means anything.
   */
  styleEmoji?: boolean;
};

type Stage = "quiz" | "loading" | "email";

export const QuizStages = ({
  profileId,
  onComplete,
  onProgress,
  onExit,
  onStage,
  styleEmoji = false,
}: Props) => {
  const [stage, setStage] = useState<Stage>("quiz");
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<QuizAnswers>({});
  const [selected, setSelected] = useState<string | null>(null);
  const [loadingStep, setLoadingStep] = useState(0);
  // handleSelect runs inside a timeout, so it must read the answers as they are
  // when it fires rather than as they were when it was created.
  const answersRef = useRef<QuizAnswers>({});
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  // The asked list depends on the answers so far, so a branch that doesn't
  // apply never costs a screen.
  const visibleFor = (given: QuizAnswers) =>
    QUESTIONS.filter((q) => !q.showIf || q.showIf(given));
  // Memoised so the hooks below can depend on it honestly: rebuilt on every
  // render, the list would be a new array each time and any effect naming it
  // would re-run on every keystroke.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const activeQuestions = useMemo(() => visibleFor(answers), [answers]);
  const question = activeQuestions[questionIndex];
  answersRef.current = answers;
  const pack = packFor(answers.style);
  const profile = profileFor(answers);

  useEffect(() => {
    analytics.track("Funnel Quiz Started", { funnel: "start_brush_quiz" });
    trackStartFunnelCustomEvent("StartFunnelQuizStarted", {
      external_profile_id: profileId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One "step viewed" per screen, matching the classic funnel's naming so the
  // two arms stay comparable in the same funnel report.
  useEffect(() => {
    analytics.track("Funnel Step Viewed", {
      funnel: "start_brush_quiz",
      phase: stage,
      question: stage === "quiz" ? activeQuestions[questionIndex]?.id : undefined,
    });
    onStage?.(stage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, questionIndex]);

  useEffect(() => {
    if (!onProgress) return;
    const done =
      stage === "quiz" ? questionIndex : stage === "loading" ? activeQuestions.length : activeQuestions.length + 1;
    onProgress(Math.round((done / (activeQuestions.length + 2)) * 100));
  }, [stage, questionIndex, onProgress, activeQuestions.length]);

  const handleSelect = useCallback(
    (value: string) => {
      if (selected) return; // ignore double taps mid-transition
      setSelected(value);
      const q = activeQuestions[questionIndex];
      analytics.track("Funnel Quiz Answer", {
        funnel: "start_brush_quiz",
        question: q.id,
        answer: value,
        step: questionIndex + 1,
      });
      trackStartFunnelCustomEvent("StartFunnelQuizAnswered", {
        external_profile_id: profileId,
        question: q.id,
        answer: value,
        step: questionIndex + 1,
      });

      if (q.id === "style") {
        try {
          localStorage.setItem(STYLE_KEY, value);
        } catch {}
      }

      setTimeout(() => {
        const nextAnswers = { ...answersRef.current, [q.id]: value };
        setAnswers(nextAnswers);
        setSelected(null);
        // Recompute against the new answers: answering the gate question is
        // what decides whether the branch behind it exists at all.
        const nextLength = visibleFor(nextAnswers).length;
        if (questionIndex + 1 < nextLength) {
          setQuestionIndex(questionIndex + 1);
        } else {
          setStage("loading");
        }
      }, 280);
    },
    [questionIndex, selected, profileId, activeQuestions]
  );

  // The "building your pack" wait. It is a real wait for nothing, and that is
  // the point: visible effort makes the result feel made-to-order rather than
  // handed out.
  useEffect(() => {
    if (stage !== "loading") return;
    setLoadingStep(0);
    const timers: ReturnType<typeof setTimeout>[] = [];
    LOADING_STEPS.forEach((_, i) => {
      timers.push(setTimeout(() => setLoadingStep(i + 1), (i + 1) * STEP_MS));
    });
    timers.push(
      setTimeout(() => {
        analytics.track("Funnel Loading Completed", { funnel: "start_brush_quiz" });
        trackStartFunnelCustomEvent("StartFunnelQuizCompleted", {
          external_profile_id: profileId,
          ...answers,
        });
        setStage("email");
      }, LOADING_STEPS.length * STEP_MS + STEP_MS)
    );
    return () => timers.forEach(clearTimeout);
  }, [stage, answers, profileId]);

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(trimmed)) {
      setEmailError("Please enter a valid email");
      return;
    }
    setEmailError("");
    try {
      localStorage.setItem(EMAIL_KEY, trimmed);
    } catch {}
    analytics.identify(profileId, { email: trimmed, ...answers });
    // Joins this funnel session to the email before the purchase, so the person
    // who pays and the person who later signs in are one person rather than two.
    analytics.alias(trimmed);
    analytics.track("Funnel Email Submitted", {
      funnel: "start_brush_quiz",
      external_profile_id: profileId,
    });
    trackFunnelLead({ external_profile_id: profileId, ...answers });

    // Create the Adapty profile server-side — fire-and-forget, never blocks.
    fetch("/.netlify/functions/create-funnel-profile", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({ email: trimmed, profileId, answers }),
    }).catch(() => {});

    onComplete(answers, trimmed);
  };

  const handleBack = () => {
    if (questionIndex > 0) setQuestionIndex(questionIndex - 1);
    else onExit?.();
  };

  if (stage === "loading") {
    return (
      <div className="flex-1 flex flex-col justify-center">
        <BuildingClip />
        <h2 className="text-2xl font-bold text-black mb-1 text-center">
          Building your pack…
        </h2>
        <p className="text-main-grey-800 text-sm text-center mb-8">
          This takes a moment — we're going through the library
        </p>

        {/* A bar per task rather than one spinner: four things visibly being
            worked through read as effort, where a turning circle reads as a
            wait. */}
        <div className="space-y-5">
          {LOADING_STEPS.map((step, i) => {
            const started = loadingStep >= i;
            const done = loadingStep > i;
            return (
              <div key={step}>
                <div className="flex items-center justify-between mb-1.5">
                  <span
                    className={`text-xs font-medium transition-colors duration-300 ${
                      started ? "text-black" : "text-main-grey-800 opacity-50"
                    }`}
                  >
                    {step}
                  </span>
                  <span
                    className={`text-xxs font-semibold transition-opacity duration-300 ${
                      done ? "text-main-blue opacity-100" : "opacity-0"
                    }`}
                  >
                    ✓
                  </span>
                </div>
                <div className="h-1.5 w-full bg-main-grey-300 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-main-blue rounded-full"
                    style={{
                      width: started ? "100%" : "0%",
                      transition: `width ${STEP_MS}ms linear`,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (stage === "email") {
    return (
      <div className="flex-1 flex flex-col justify-center">
        <div className="relative mx-auto mb-6">
          <img
            src={styleImageFor(answers.style)}
            alt=""
            className="w-44 h-44 object-cover rounded-3xl shadow-lg"
          />
          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-main-blue text-white text-xxs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
            ✓ Pack curated
          </div>
        </div>
        <h2 className="text-2xl font-bold text-black leading-tight mb-2 text-center">
          Your <span className="text-main-blue">{pack.name}</span> pack is ready 🎉
        </h2>

        {/* The read-back. Answering five questions has to buy something, and
            what it buys is being described to yourself: the visitor sees their
            own answers turned into a profile before being asked for anything. */}
        <div className="bg-main-grey-200 rounded-2xl px-4 py-3 mb-5 mt-3">
          <p className="text-black text-xxs font-bold uppercase tracking-wide mb-2">
            Your artist profile
          </p>
          <div className="flex flex-col gap-1.5">
            {[
              // Came for it → first line. This is the one row the visitor can
              // check against their own reason for being here, so it earns the
              // rest of the list.
              ["Looking for", profile.lookingFor],
              ["Draws", profile.style],
              ["Level", profile.level],
              ["Practice", profile.frequency],
              ["Drawing for", profile.motivation],
              ["Biggest gap", profile.gap],
              ["Matched", `${pack.count} brushes`],
            ]
              .filter(([, value]) => value)
              .map(([label, value]) => (
                <div key={label} className="flex justify-between items-baseline gap-3">
                  <span className="text-main-grey-800 text-xxs">{label}</span>
                  <span className="text-black text-xxs font-semibold text-right">
                    {value}
                  </span>
                </div>
              ))}
          </div>
        </div>

        <p className="text-main-grey-800 text-sm text-center mb-4">
          Enter your email to save it and open it on all your devices.
        </p>
        <form onSubmit={handleEmailSubmit} noValidate>
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="Your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full border-2 border-main-grey-300 focus:border-main-blue outline-none rounded-2xl px-4 py-4 text-black text-sm mb-2"
          />
          {emailError && <p className="text-main-red-900 text-xxs mb-2">{emailError}</p>}
          <button
            type="submit"
            className="w-full bg-main-blue text-white font-semibold py-4 rounded-2xl text-lg mt-2 active:scale-[0.98] transition-transform"
          >
            See my pack
          </button>
        </form>
        <p className="text-main-grey-800 text-xxs text-center mt-4">
          🔒 We respect your privacy. No spam, ever.
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col pt-2">
      <BuildingClipPreload />
      <div className="flex items-center gap-3 mb-3">
        <button
          onClick={handleBack}
          aria-label="Back"
          className="text-main-grey-800 text-xl leading-none p-1"
        >
          ←
        </button>
        <span className="text-main-grey-800 text-xxs font-medium">
          Question {questionIndex + 1} of {activeQuestions.length}
        </span>
      </div>

      <h2 className="text-2xl font-bold text-black leading-tight mb-1">
        {question.title}
      </h2>
      {question.subtitle && (
        <p className="text-main-grey-800 text-sm mb-4">{question.subtitle}</p>
      )}

      {question.id === "style" ? (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3">
            {STYLE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => handleSelect(opt.value)}
                className={`rounded-2xl overflow-hidden border-2 text-left transition-colors ${
                  selected === opt.value
                    ? "border-main-blue"
                    : "border-main-grey-300 active:border-main-grey-400"
                }`}
              >
                {styleEmoji ? (
                  <span className="flex flex-col items-center justify-center gap-2 min-h-[104px] px-3 py-5 bg-white">
                    <span className="text-3xl leading-none">{opt.emoji}</span>
                    <span className="text-black text-xs font-semibold text-center leading-tight">
                      {opt.label}
                    </span>
                  </span>
                ) : (
                  <>
                    <img src={opt.image} alt={opt.label} className="w-full h-28 object-cover" />
                    <span
                      className={`block px-3 py-2 text-xs font-medium ${
                        selected === opt.value
                          ? "bg-light-blue text-main-blue"
                          : "bg-white text-black"
                      }`}
                    >
                      {opt.label}
                    </span>
                  </>
                )}
              </button>
            ))}
          </div>
          <div className="mt-3">
            {STYLE_EXTRAS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => handleSelect(opt.value)}
                className={`w-full rounded-2xl border-2 py-4 px-3 flex items-center justify-center gap-2 transition-colors ${
                  selected === opt.value
                    ? "border-main-blue bg-light-blue"
                    : "border-main-grey-300 bg-white active:bg-main-grey-200"
                }`}
              >
                <span className="text-xl leading-none">{opt.emoji}</span>
                <span className="text-black text-sm font-semibold">{opt.label}</span>
              </button>
            ))}
          </div>
        </>
      ) : (
        /* Two-column tiles with the emoji carrying the weight — a stack of text
           rows reads like a form, and eight of those in a row reads like work. */
        <div className="mt-4 grid grid-cols-2 gap-3">
          {question.options.map((opt) =>
            opt.image ? (
              <button
                key={opt.value}
                onClick={() => handleSelect(opt.value)}
                className={`rounded-2xl overflow-hidden border-2 text-left transition-colors ${
                  selected === opt.value
                    ? "border-main-blue"
                    : "border-main-grey-300 active:border-main-grey-400"
                }`}
              >
                {/* No lazy loading: these cards are the screen, and deferring
                    them just leaves the question blank while they arrive. */}
                <img src={opt.image} alt={opt.label} className="w-full h-24 object-cover" />
                <span
                  className={`block px-2 py-2 text-xxs font-semibold text-center leading-tight ${
                    selected === opt.value
                      ? "bg-light-blue text-main-blue"
                      : "bg-white text-black"
                  }`}
                >
                  {opt.label}
                </span>
              </button>
            ) : (
              <button
                key={opt.value}
                onClick={() => handleSelect(opt.value)}
                className={`rounded-2xl border-2 px-3 py-5 flex flex-col items-center justify-center gap-2 min-h-[104px] transition-colors ${
                  selected === opt.value
                    ? "border-main-blue bg-light-blue"
                    : "border-main-grey-300 bg-white active:bg-main-grey-200"
                }`}
              >
                <span className="text-3xl leading-none">{opt.emoji}</span>
                <span className="text-black text-xs font-semibold text-center leading-tight">
                  {opt.label}
                </span>
              </button>
            )
          )}
        </div>
      )}

      <div className="mt-auto pt-6">
        <div className="bg-main-grey-200 rounded-2xl px-4 py-3 flex items-start gap-3">
          <img
            src={TESTIMONIALS[questionIndex % TESTIMONIALS.length].image}
            alt=""
            className="w-8 h-8 rounded-full object-cover shrink-0"
          />
          <div>
            <div className="flex items-center gap-2">
              <span className="text-black text-xxs font-semibold">
                {TESTIMONIALS[questionIndex % TESTIMONIALS.length].name}
              </span>
              <Stars size={11} />
            </div>
            <p className="text-main-grey-800 text-xxs mt-0.5 leading-snug">
              "{TESTIMONIALS[questionIndex % TESTIMONIALS.length].text}"
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
